import React from 'react'
import axios from 'axios'
import styled from 'styled-components/macro'
import { useHistory } from 'react-router-dom'
import { Input, FormSection, Spinner } from 'components'

const LayoutWrapper = styled.div`
	max-width: 600px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
`

const CompanyItem = styled.div`
	padding: 16px 18px;
	border: 1.5px solid ${props => props.theme.lineColor};
	border-radius: ${props => props.theme.radius};
	background: #fff;
	cursor: pointer;
	margin-bottom: 10px;
	font-weight: 600;
	color: ${props => props.theme.navyColor};
	transition: border-color 0.15s ease, background 0.15s ease;
	:hover {
		border-color: ${props => props.theme.primaryColor};
		background: ${props => props.theme.softColor};
	}
	&.active {
		outline: 2px solid ${props => props.theme.primaryColor};
		background: #fafafa;
	}
`

const Wrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: 16px;
	@media (max-width: 480px) {
		display: flex;
		flex-wrap: wrap;
	}
`

const Field = styled.div`
	min-width: 0;
	flex: 1 1 100%;
	grid-column: ${props => (props.wide ? '1 / span 2' : '')};
`

const CompanySearch = () => {
	const history = useHistory()
	const [data, setData] = React.useState([])
	const [query, setQuery] = React.useState('')
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState(null)
	const [emptyHint, setEmptyHint] = React.useState(null)
	const [selecting, setSelecting] = React.useState(null)
	const debouncedQuery = useDebounce(query, 300)

	const fetchData = q => {
		if (!q || q.length < 2) {
			setData([])
			setError(null)
			setEmptyHint(null)
			return
		}
		setLoading(true)
		setError(null)
		setEmptyHint(null)
		axios
			.get(`/api/ytj/search?q=${encodeURIComponent(q)}&limit=10`)
			.then(res => {
				const payload = res.data
				const list = Array.isArray(payload)
					? payload
					: payload?.results || []
				setData(list)
				if (list.length === 0) {
					setEmptyHint(
						'Ei osakeyhtiöitä (OY/OYJ) tällä haulla. Kokeile rekisteröityä toiminimeä tai Y-tunnusta (esim. 1234567-8). Toiminimi-yrittäjät eivät näy YTJ:n avoimessa haussa.'
					)
				}
			})
			.catch(err => {
				setData([])
				setEmptyHint(null)
				setError(
					err?.response?.status === 503
						? 'YTJ on tilapäisesti täynnä. Odota 1–2 minuuttia tai hae Y-tunnuksella.'
						: 'Yrityshaku epäonnistui. Yritä uudelleen.'
				)
			})
			.finally(() => setLoading(false))
	}

	const selectCompany = companyId => {
		if (selecting) {
			return
		}
		setSelecting(companyId)
		setError(null)
		axios
			.post(`/api/ytj/companies/${encodeURIComponent(companyId)}/import`)
			.then(() => {
				history.push({
					pathname: '/form/new',
					state: { companyId },
				})
			})
			.catch(err => {
				setSelecting(null)
				const msg = err?.response?.data
				setError(
					err?.response?.status === 422 && typeof msg === 'string'
						? msg
						: 'Yrityksen tietojen lataus epäonnistui. Yritä uudelleen.'
				)
			})
	}

	React.useEffect(() => fetchData(debouncedQuery), [debouncedQuery])

	return (
		<LayoutWrapper>
			<FormSection>
				<h2>Aloita hakemuksen tekeminen</h2>
				<p className="lead">
					Hae yritys nimellä tai Y-tunnuksella. Tiedot haetaan YTJ:stä, ja
					voit tarkistaa ne seuraavassa vaiheessa.
				</p>
				<div>
					<Wrapper>
						<Field wide>
							<Input
								value={query}
								onChange={e => {
									setQuery(e.target.value)
								}}
								type="text"
								label={'Yrityksen nimi tai Y-tunnus'}
								placeholder="Esim. Esimerkki Oy tai 1234567-8"
							/>
							{loading && !selecting && <Spinner text="Haetaan YTJ:stä…" />}
							{error && <p style={{ color: '#c00' }}>{error}</p>}
							{!loading && !error && emptyHint && (
								<p style={{ color: '#555' }}>{emptyHint}</p>
							)}
							{selecting && (
								<Spinner
									block
									size={34}
									text="Haetaan yrityksen tietoja, tämä kestää muutaman sekunnin…"
								/>
							)}
							<div style={selecting ? { display: 'none' } : undefined}>
								{data.map(item => (
									<CompanyItem
										key={item.id}
										onClick={() => selectCompany(item.id)}
									>
										{item.name} ({item.id})
										{item.companyForm ? ` – ${item.companyForm}` : ''}
										{item.courtName ? (
											<div style={{ fontSize: 15, fontWeight: 400, color: '#45535d', marginTop: 4 }}>
												Kotipaikka: {item.hometown || item.city} · {item.courtName}
											</div>
										) : null}
									</CompanyItem>
								))}
							</div>
						</Field>
					</Wrapper>
				</div>
			</FormSection>
		</LayoutWrapper>
	)
}

function useDebounce(value, delay) {
	const [debouncedValue, setDebouncedValue] = React.useState(value)

	React.useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

export default CompanySearch
